import { AfterViewInit, Component, OnDestroy, Type, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ButtonModule } from '@openng/optimus-ui/button';
import { NgClass } from '@angular/common';
import { DynamicRouteHandlingService } from '../../services/dynamic-route-handling.service';

/**
 * Allowed positions for the PrimeNG dialog. Mirrors p-dialog's [position] input.
 */
export type DynamicRoutePrimengPosition =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'center'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright';

export interface DynamicRoutePrimengData {
  component: Type<unknown>;
  modalTitle: string;
  position?: DynamicRoutePrimengPosition;
  dialogStyle?: Record<string, string>;
  modal?: boolean;
  closable?: boolean;
  maximizable?: boolean;
  dismissableMask?: boolean;
  closeOnEscape?: boolean;
  contentStyleClass?: string;
  returnRoute: string;
}

@Component({
  selector: 'lib-dynamic-route-handler-primeng',
  standalone: true,
  imports: [NgClass, DialogModule, ButtonModule],
  templateUrl: './dynamic-route-handler-primeng.component.html',
  styles: ``
})
export class DynamicRouteHandlerPrimengComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dynamicComponentRef', { read: ViewContainerRef, static: false })
  dynamicComponentRef!: ViewContainerRef;

  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #dynamicRouteHandlingService = inject(DynamicRouteHandlingService);

  private hasClosed = false;
  private readonly routeComponent: Type<unknown> | null =
    (this.#route.snapshot.data['component'] as Type<unknown> | undefined) ?? null;

  visible = this.routeComponent !== null;

  modalTitle: string = this.#route.snapshot.data['modalTitle'] ?? '';
  position: DynamicRoutePrimengPosition = this.#route.snapshot.data['position'] ?? 'right';
  modal: boolean = this.#route.snapshot.data['modal'] ?? true;
  closable: boolean = this.#route.snapshot.data['closable'] ?? true;
  maximizable: boolean = this.#route.snapshot.data['maximizable'] ?? false;
  dismissableMask: boolean = this.#route.snapshot.data['dismissableMask'] ?? false;
  closeOnEscape: boolean = this.#route.snapshot.data['closeOnEscape'] ?? false;
  contentStyleClass: string | null = this.#route.snapshot.data['contentStyleClass'] ?? null;

  ngAfterViewInit(): void {
    if (!this.routeComponent) return;
    setTimeout(() => this.createComponent(this.routeComponent!), 0);
  }

  private createComponent(component: Type<unknown>): void {
    if (!this.dynamicComponentRef) return;
    this.dynamicComponentRef.clear();
    this.#dynamicRouteHandlingService.componentRef.next(this.dynamicComponentRef.createComponent(component));
  }

  onDialogHide(): void {
    this.closeModal();
  }

  closeModal(): void {
    if (this.hasClosed) return;
    this.hasClosed = true;
    this.visible = false;
    setTimeout(() => this.destroyComponent(), 0);
    this.#router.navigate([this.#route.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
  }

  private destroyComponent(): void {
    if (this.#dynamicRouteHandlingService.componentRef.value) {
      this.#dynamicRouteHandlingService.componentRef.value.destroy();
      this.#dynamicRouteHandlingService.componentRef.next(null);
    }
    if (this.dynamicComponentRef) {
      this.dynamicComponentRef.clear();
    }
  }

  ngOnDestroy(): void {
    this.hasClosed = true;
    this.destroyComponent();
  }
}
